/**
 * Admin panel contracts (§9). Every route behind these is gated on
 * `users.is_admin` — a flag that is only ever set out-of-band, never through
 * the API.
 *
 * Scope is deliberately small and internal: user management, user stats, and
 * AWS spend.
 */

import { z } from 'zod';

import { role } from './enums.js';
import { isoDate, uuid } from './primitives.js';

// ---------------------------------------------------------------------------
// User management
// ---------------------------------------------------------------------------

export const adminUserRow = z.object({
  id: uuid,
  email: z.string().email(),
  name: z.string(),
  isAdmin: z.boolean(),
  disabledAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  garageCount: z.number().int(),
  carCount: z.number().int(),
});
export type AdminUserRow = z.infer<typeof adminUserRow>;

export const listUsersQuery = z.object({
  /** Matches name or email, case-insensitively. */
  q: z.string().trim().max(120).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListUsersQuery = z.infer<typeof listUsersQuery>;

export const listUsersResponse = z.object({
  users: z.array(adminUserRow),
  total: z.number().int(),
});
export type ListUsersResponse = z.infer<typeof listUsersResponse>;

/** A user's memberships, for the detail drawer. */
export const adminUserDetail = z.object({
  user: adminUserRow,
  memberships: z.array(
    z.object({
      garageId: uuid,
      garageName: z.string(),
      role,
      isPublic: z.boolean(),
      carCount: z.number().int(),
    }),
  ),
});
export type AdminUserDetail = z.infer<typeof adminUserDetail>;

/**
 * Disabling is reversible and keeps the data; deletion is not. They are
 * separate routes precisely so the destructive one is never the default.
 */
export const setUserDisabledRequest = z.object({
  disabled: z.boolean(),
});
export type SetUserDisabledRequest = z.infer<typeof setUserDisabledRequest>;

/**
 * Deleting an account takes the garages it *owns* with it — cars, items,
 * events, logs and all (§11, Privacy). Confirmation is the typed-out email, so
 * an admin cannot nuke the wrong row with a stray click.
 */
export const deleteUserRequest = z.object({
  confirmEmail: z.string().trim().toLowerCase().email(),
});
export type DeleteUserRequest = z.infer<typeof deleteUserRequest>;

export const deleteUserResponse = z.object({
  deleted: z.object({
    users: z.number().int(),
    garages: z.number().int(),
    cars: z.number().int(),
    items: z.number().int(),
    events: z.number().int(),
    serviceLogs: z.number().int(),
  }),
});
export type DeleteUserResponse = z.infer<typeof deleteUserResponse>;

export const adminUserParam = z.object({ userId: uuid });

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export const statsResponse = z.object({
  totals: z.object({
    users: z.number().int(),
    garages: z.number().int(),
    publicGarages: z.number().int(),
    cars: z.number().int(),
    drivers: z.number().int(),
    items: z.number().int(),
    events: z.number().int(),
    serviceLogs: z.number().int(),
  }),
  /** Signups per day over the requested window, zero-filled so the chart has no gaps. */
  signups: z.array(z.object({ date: isoDate, count: z.number().int() })),
  /**
   * Active = logged a service or an event in the window. Cheap to compute and
   * a better signal than "signed in", which we do not record.
   */
  activeUsers: z.object({
    last7Days: z.number().int(),
    last30Days: z.number().int(),
  }),
});
export type StatsResponse = z.infer<typeof statsResponse>;

export const statsQuery = z.object({
  days: z.coerce.number().int().min(7).max(365).default(90),
});
export type StatsQuery = z.infer<typeof statsQuery>;

// ---------------------------------------------------------------------------
// AWS costs
// ---------------------------------------------------------------------------

/**
 * Shaped after DragDuro's `/api/admin/cost`, which has this in production and
 * learned three things the hard way — all of them carried over here:
 *
 *  - Cost Explorer bills **$0.01 per request**, so the payload is cached
 *    (6 hours) rather than fetched per page load. `cached` says which you got.
 *  - CE reports with roughly a 24-hour lag, so the most recent day is partial
 *    and must not be drawn as a real drop in spend.
 *  - The endpoint always answers 200. If CE is unavailable or the caller's role
 *    lacks `ce:GetCostAndUsage`, `available: false` comes back and the UI hides
 *    the card. A cost widget must never be able to break the admin page.
 */
export const costsQuery = z.object({
  days: z.coerce.number().int().min(7).max(365).default(90),
});
export type CostsQuery = z.infer<typeof costsQuery>;

export const COST_CACHE_TTL_MS = 6 * 60 * 60 * 1000;

export const costsResponse = z.discriminatedUnion('available', [
  z.object({
    available: z.literal(true),
    currency: z.string(),
    /** Daily unblended cost, oldest first. */
    series: z.array(z.object({ date: isoDate, cost: z.number(), partial: z.boolean() })),
    byService: z.array(z.object({ service: z.string(), amount: z.number() })),
    total: z.number(),
    fetchedAt: z.string().datetime(),
    cached: z.boolean(),
  }),
  z.object({
    available: z.literal(false),
    /** For the operator's eyes — the UI just hides the card. */
    reason: z.string(),
  }),
]);
export type CostsResponse = z.infer<typeof costsResponse>;
